// UpgradeModal.jsx - Beautiful upgrade prompt like Claude
import React from 'react';
import './UpgradeModal.css';
import { PLAN_LIMITS } from '../constants/planLimits';

function UpgradeModal({ 
  isOpen, 
  onClose, 
  currentPlan = 'free',
  limitType = 'projects', // 'projects', 'duration', 'mp4'
  usage = {},
  suggestedPlan = null
}) {
  if (!isOpen) return null;

  // Determine which plan to suggest
  const getRecommendedPlan = () => {
    if (suggestedPlan) {
      // Normalize plan name to lowercase to match PLAN_LIMITS keys
      const normalizedPlan = suggestedPlan.toLowerCase();
      // Verify the plan exists in PLAN_LIMITS
      if (PLAN_LIMITS[normalizedPlan]) {
        return normalizedPlan;
      }
    }
    
    if (currentPlan === 'free') return 'starter';
    if (currentPlan === 'starter') return 'creator';
    if (currentPlan === 'creator') return 'professional';
    if (currentPlan === 'professional') return 'professional'; // Already at top
    return 'starter';
  };

  const recommendedPlan = getRecommendedPlan();
  const currentPlanData = PLAN_LIMITS[currentPlan];
  const nextPlanData = PLAN_LIMITS[recommendedPlan];

  // Safety check - if nextPlanData is undefined, close modal and log error
  if (!nextPlanData) {
    console.error(`❌ Invalid plan recommended: ${recommendedPlan}. Available plans:`, Object.keys(PLAN_LIMITS));
    onClose();
    return null;
  }

  // Generate limit message based on type
  const getLimitMessage = () => {
    switch (limitType) {
      case 'projects':
        if (currentPlan === 'free') {
          // For free plan, show individual project types
          const currentUsageItems = [];
          
          // Check which limit was actually exceeded
          const promptExceeded = (usage.prompt || 0) >= currentPlanData.monthlyProjects.prompt;
          const audioExceeded = (usage.audio || 0) >= currentPlanData.monthlyProjects.audio;
          
          if (promptExceeded) {
            currentUsageItems.push(`Prompt projects: ${usage.prompt || 0}/${currentPlanData.monthlyProjects.prompt} (limit reached)`);
          }
          if (audioExceeded) {
            currentUsageItems.push(`Audio projects: ${usage.audio || 0}/${currentPlanData.monthlyProjects.audio} (limit reached)`);
          }
          
          // If neither is exceeded but we're here, show both
          if (currentUsageItems.length === 0) {
            currentUsageItems.push(
              `Prompt projects: ${usage.prompt || 0}/${currentPlanData.monthlyProjects.prompt}`,
              `Audio projects: ${usage.audio || 0}/${currentPlanData.monthlyProjects.audio}`
            );
          }
          
          return {
            title: `${currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} Plan Limit Exceeded`,
            description: `You've used all your monthly projects. Upgrade to continue creating.`,
            currentUsage: currentUsageItems
          };
        }
        return {
          title: `${currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} Plan Limit Exceeded`,
          description: `You've used all your monthly projects. Upgrade to continue creating.`,
          currentUsage: [
            `Total projects: ${(usage.prompt || 0) + (usage.audio || 0)}/${currentPlanData.monthlyProjects.total} (limit reached)`
          ]
        };
      
      case 'duration':
        return {
          title: 'Audio File Too Long',
          description: `Your ${currentPlan} plan supports up to ${Math.floor(currentPlanData.maxAudioDuration / 60)}-minute audio files`,
          currentUsage: [
            `Your file: ${usage.fileDuration ? Math.floor(usage.fileDuration / 60) : '?'} minutes`,
            `Current limit: ${Math.floor(currentPlanData.maxAudioDuration / 60)} minutes (exceeded)`
          ]
        };
      
      case 'mp4':
        return {
          title: 'HD Video Limit Exceeded',
          description: `You've used all your MP4 video downloads for this month`,
          currentUsage: [
            `MP4 downloads: ${usage.mp4 || 0}/${currentPlanData.mp4Downloads} (limit reached)`
          ]
        };
      
      default:
        return {
          title: 'Upgrade to Unlock More',
          description: 'Get more features with a paid plan',
          currentUsage: []
        };
    }
  };

  const limitInfo = getLimitMessage();

  // Get upgrade benefits based on what user needs
  const getUpgradeBenefits = () => {
    const benefits = [];

    // Projects benefit
    if (limitType === 'projects' || limitType === 'duration') {
      if (recommendedPlan === 'starter') {
        benefits.push('70 projects per month (60 prompt + 10 audio)');
      } else if (recommendedPlan === 'creator') {
        benefits.push('200 projects per month (150 prompt + 50 audio)');
      } else if (recommendedPlan === 'professional') {
        benefits.push('350 projects per month (250 prompt + 100 audio)');
      }
    }

    // Duration benefit
    if (limitType === 'duration' || limitType === 'projects') {
      const durationMinutes = Math.floor(nextPlanData.maxAudioDuration / 60);
      benefits.push(`${durationMinutes}-minute audio uploads`);
    }

    // MP4 benefit
    if (limitType === 'mp4' || limitType === 'projects') {
      benefits.push(`${nextPlanData.mp4Downloads} MP4 video downloads`);
    }

    // Additional benefits
    benefits.push('Unlimited PNG & PPTX exports');
    
    if (recommendedPlan === 'creator' || recommendedPlan === 'professional') {
      benefits.push('Priority rendering queue');
    }

    if (recommendedPlan === 'professional') {
      benefits.push('Priority support');
    }

    return benefits;
  };

  const benefits = getUpgradeBenefits();

  const handleUpgrade = () => {
    // Navigate to pricing page with pre-selected plan
    window.location.href = `/pricing-plans?plan=${recommendedPlan}`;
  };

  return (
    <div className="upgrade-modal-overlay" onClick={onClose}>
      <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
        <button className="upgrade-modal-close" onClick={onClose}>
          ×
        </button>

        <div className="upgrade-modal-header">
          <div className="upgrade-modal-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
            </svg>
          </div>
          <h2>{limitInfo.title}</h2>
          <p className="upgrade-modal-subtitle">{limitInfo.description}</p>
        </div>

        {limitInfo.currentUsage.length > 0 && (
          <div className="upgrade-current-usage">
            <h3>Current Status</h3>
            <ul>
              {limitInfo.currentUsage.map((item, index) => (
                <li key={index}>
                  <span className="usage-bullet">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="upgrade-benefits">
          <h3>Upgrade to {nextPlanData.name} and unlock:</h3>
          <ul>
            {benefits.map((benefit, index) => (
              <li key={index}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        <div className="upgrade-pricing">
          <span className="upgrade-price">${nextPlanData.price.toFixed(2)}</span>
          <span className="upgrade-period">/month</span>
        </div>

        <div className="upgrade-modal-actions">
          <button className="upgrade-btn-secondary" onClick={onClose}>
            Maybe Later
          </button>
          <button className="upgrade-btn-primary" onClick={handleUpgrade}>
            Upgrade to {nextPlanData.name}
          </button>
        </div>

        <p className="upgrade-modal-footer">
          Cancel anytime. No hidden fees.
        </p>
      </div>
    </div>
  );
}

export default UpgradeModal;